import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Container,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  People as PeopleIcon,
  School as SchoolIcon,
  ArrowForward as ArrowForwardIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const SignUpSelection = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleCaregiverSignUp = () => {
    navigate('/signup/caregiver');
  };

  const handleStudentSignUp = () => {
    navigate('/signup/student');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  return (
    <Container maxWidth={false} sx={{ mt: 2, mb: 4, px: 4 }}>
      {/* Header Section */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography 
          variant={isMobile ? "h4" : "h3"} 
          component="h1" 
          gutterBottom 
          color="primary"
          sx={{ 
            fontWeight: 'bold',
            mb: 1
          }}
        >
          RoboJackets Registration
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary" 
          sx={{ 
            mb: 3,
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          Choose your registration type to get started, or login if you already have an account
        </Typography>
      </Box>

      {/* Selection Cards */}
      <Box 
        sx={{ 
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: 'wrap',
          gap: 3,
          justifyContent: 'center',
          maxWidth: '1200px',
          mx: 'auto'
        }}
      >
        {/* Login Card */}
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', lg: '1 1 calc(33.333% - 16px)' }, minWidth: 0 }}>
          <Card 
            elevation={3} 
            sx={{ 
              height: '100%',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[8],
              }
            }}
            onClick={handleLogin}
          >
            <CardContent sx={{ p: 4, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <LockIcon sx={{ fontSize: 64, color: 'primary.main' }} />
              </Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                Login
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, flexGrow: 1 }}>
                I already have an account and want to access my dashboard. 
                I can login as an administrator, team supervisor, or household member.
              </Typography>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{ 
                  mt: 'auto',
                  py: 1.5,
                  px: 3
                }}
              >
                Login to Dashboard
              </Button>
            </CardContent>
          </Card>
        </Box>

        {/* Caregiver Card */}
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', lg: '1 1 calc(33.333% - 16px)' }, minWidth: 0 }}>
          <Card 
            elevation={3} 
            sx={{ 
              height: '100%',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[8],
              }
            }}
            onClick={handleCaregiverSignUp}
          >
            <CardContent sx={{ p: 4, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <PeopleIcon sx={{ fontSize: 64, color: 'primary.main' }} />
              </Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                Caregiver Registration
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, flexGrow: 1 }}>
                I am a parent, guardian, or caregiver registering for myself and my family. 
                I will create a household and can add my children/students later.
              </Typography>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{ 
                  mt: 'auto',
                  py: 1.5,
                  px: 3
                }}
              >
                Register as Caregiver
              </Button>
            </CardContent>
          </Card>
        </Box>

        {/* Student Card */}
        {/* Temporarily hidden
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', lg: '1 1 calc(33.333% - 16px)' }, minWidth: 0 }}>
          <Card 
            elevation={3} 
            sx={{ 
              height: '100%',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[8],
              }
            }}
            onClick={handleStudentSignUp}
          >
            <CardContent sx={{ p: 4, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column' }}>
              <SchoolIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                Student Registration
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, flexGrow: 1 }}>
                I am a student registering for myself. My parent/guardian has already 
                registered and I will be added to their household.
              </Typography>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{ 
                  mt: 'auto',
                  py: 1.5,
                  px: 3
                }}
              >
                Register as Student
              </Button>
            </CardContent>
          </Card>
        </Box>
        */}
      </Box>

      {/* Additional Information */}
      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto', mb: 2 }}>
          <strong>Not sure which to choose?</strong> If you're a parent or guardian, choose "Caregiver Registration". 
          If you're a student and your parent has already registered, choose "Student Registration".
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto', mb: 2 }}>
          <strong>Already registered?</strong> Click the "Login" card above to access your dashboard 
          and manage your information.
        </Typography>
        <Button
          variant="text"
          size="small"
          onClick={() => navigate('/privacy-policy')}
          sx={{ 
            color: 'text.secondary',
            textDecoration: 'underline',
            '&:hover': {
              textDecoration: 'none'
            }
          }}
        >
          Privacy Policy
        </Button>
      </Box>
    </Container>
  );
};

export default SignUpSelection; 